/**
 * Evaluación determinista de unidades de evaluación (UE) de un hábito.
 * Implementa FR-SCH, FR-STA, FR-VAC y FR-HAB-04..06.
 */
import {
  addDays,
  alignBack,
  daysInMonth,
  diffDays,
  eachDay,
  isoWeekday,
  maxDate,
  minDate,
  parseLocalDate,
  type LocalDate,
} from './dates.js';
import { clampRange, dayKey, monthPeriods, weekPeriods, type Period } from './periods.js';
import type {
  EngineSettings,
  EntryLike,
  EvaluationInput,
  HabitDef,
  HabitVersion,
  MoveLike,
  OverrideLike,
  Schedule,
  UEKind,
  UEResult,
  UEStatus,
  VacationLike,
} from './types.js';

const EPS = 1e-9;
const FAR_PAST = '1900-01-01';
const FAR_FUTURE = '2999-12-31';

export function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function isQuota(s: Schedule): s is Extract<Schedule, { type: 'weekly_quota' | 'monthly_quota' }> {
  return s.type === 'weekly_quota' || s.type === 'monthly_quota';
}

export function sortedVersions(habit: HabitDef): HabitVersion[] {
  return [...habit.versions].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1));
}

/** Último día en que el hábito se evalúa (inclusive) o null si no tiene fin. */
export function activeEnd(habit: HabitDef): LocalDate {
  let end = habit.endDate ?? FAR_FUTURE;
  if (habit.archivedAt) end = minDate(end, addDays(habit.archivedAt, -1));
  return end;
}

/** ¿El día `d` está programado según una repetición basada en días? */
export function isDayScheduled(schedule: Schedule, d: LocalDate, anchor: LocalDate): boolean {
  switch (schedule.type) {
    case 'daily':
      return true;
    case 'weekdays': {
      if (!schedule.days.includes(isoWeekday(d))) return false;
      if (!schedule.everyWeeks || schedule.everyWeeks <= 1) return true;
      // shortcut: semanas ISO (de lunes a domingo) aunque el usuario empiece la semana otro día; pasar los ajustes si alguien lo pide.
      const weeks = Math.floor(diffDays(alignBack(anchor, 1), d) / 7);
      return weeks >= 0 && weeks % schedule.everyWeeks === 0;
    }
    case 'yearly': {
      const a = parseLocalDate(anchor);
      const { y, m, d: day } = parseLocalDate(d);
      // Un 29 de febrero se celebra el 28 en los años no bisiestos.
      return m === a.m && day === Math.min(a.d, daysInMonth(y, m));
    }
    case 'interval_days': {
      const diff = diffDays(anchor, d);
      return diff >= 0 && diff % schedule.every === 0;
    }
    case 'monthdays': {
      const { y, m, d: day } = parseLocalDate(d);
      const last = daysInMonth(y, m);
      if (schedule.lastDay && day === last) return true;
      // Días inexistentes en el mes se trasladan al último día (FR-SCH-04).
      return schedule.days.some((sd) => (sd > last ? last : sd) === day);
    }
    default:
      return false;
  }
}

/**
 * Traslados vigentes de un hábito (FR-SCH-12), separados en salidas y llegadas.
 * Un día que es a la vez origen y destino se trata como origen: el traslado
 * encadenado no está permitido y la validación (`checkMove`) lo rechaza antes.
 */
export interface MoveIndex {
  out: Set<LocalDate>;
  in: Set<LocalDate>;
}

export function indexMoves(moves: MoveLike[] | undefined, habitId: string): MoveIndex {
  const idx: MoveIndex = { out: new Set(), in: new Set() };
  for (const m of moves ?? []) {
    if (m.deletedAt || m.habitId !== habitId || m.fromDate === m.toDate) continue;
    idx.out.add(m.fromDate);
    idx.in.add(m.toDate);
  }
  return idx;
}

interface Chain {
  versions: HabitVersion[];
  start: LocalDate;
  end: LocalDate;
  periodType: UEKind;
}

/** Agrupa versiones consecutivas que comparten tipo de periodo de cuota. */
function buildChains(versions: HabitVersion[]): Chain[] {
  const chains: Chain[] = [];
  versions.forEach((v, i) => {
    const start = i === 0 ? FAR_PAST : v.effectiveFrom;
    const next = versions[i + 1];
    const end = next ? addDays(next.effectiveFrom, -1) : FAR_FUTURE;
    const periodType: UEKind =
      v.schedule.type === 'weekly_quota' ? 'week' : v.schedule.type === 'monthly_quota' ? 'month' : 'day';
    const prev = chains[chains.length - 1];
    if (prev && periodType !== 'day' && prev.periodType === periodType) {
      prev.versions.push(v);
      prev.end = end;
    } else {
      chains.push({ versions: [v], start, end, periodType });
    }
  });
  return chains;
}

interface RawUE {
  key: string;
  kind: UEKind;
  start: LocalDate;
  end: LocalDate;
  activeStart: LocalDate;
  activeEnd: LocalDate;
  nominal: number;
  version: HabitVersion;
}

/** Genera las UE programadas de un hábito dentro de [from, to]. */
export function generateUEs(
  habit: HabitDef,
  settings: EngineSettings,
  from: LocalDate,
  to: LocalDate,
  moves?: MoveLike[],
): RawUE[] {
  if (habit.kind === 'task' || habit.versions.length === 0) return [];
  const active = clampRange({ start: habit.startDate, end: activeEnd(habit) }, { start: from, end: to });
  const habitActive = { start: habit.startDate, end: activeEnd(habit) };
  if (!active) return [];
  const out: RawUE[] = [];
  const mv = indexMoves(moves, habit.id);

  for (const chain of buildChains(sortedVersions(habit))) {
    const chainRange = clampRange({ start: chain.start, end: chain.end }, active);
    if (!chainRange) continue;

    if (chain.periodType === 'day') {
      const v = chain.versions[0]!;
      for (const d of eachDay(chainRange.start, chainRange.end)) {
        // El día trasladado deja de estar programado; el destino lo está aunque la repetición no lo diga.
        if (mv.out.has(d)) continue;
        if (isDayScheduled(v.schedule, d, habit.startDate) || mv.in.has(d)) {
          out.push({ key: dayKey(d), kind: 'day', start: d, end: d, activeStart: d, activeEnd: d, nominal: 1, version: v });
        }
      }
      continue;
    }

    const periods: Period[] =
      chain.periodType === 'week'
        ? weekPeriods(settings, chainRange.start, chainRange.end)
        : monthPeriods(chainRange.start, chainRange.end);
    for (const p of periods) {
      const window = clampRange(p, {
        start: maxDate(chain.start, habitActive.start),
        end: minDate(chain.end, habitActive.end),
      });
      if (!window) continue;
      // FR-SCH-10: la UE se evalúa con la última versión de la cadena vigente al final del periodo.
      let owner = chain.versions[0]!;
      for (const v of chain.versions) if (v.effectiveFrom <= p.end) owner = v;
      out.push({
        key: p.key,
        kind: chain.periodType,
        start: p.start,
        end: p.end,
        activeStart: window.start,
        activeEnd: window.end,
        nominal: p.nominal,
        version: owner,
      });
    }
  }
  return out;
}

function vacationApplies(v: VacationLike, habitId: string): boolean {
  if (v.deletedAt) return false;
  return v.scope === 'all' || (v.habitIds ?? []).includes(habitId);
}

export function inVacation(vacations: VacationLike[], habitId: string, d: LocalDate): boolean {
  return vacations.some((v) => vacationApplies(v, habitId) && v.startDate <= d && d <= v.endDate);
}

export function dailyTotals(entries: EntryLike[], habitId: string): Map<LocalDate, number> {
  const sums = new Map<LocalDate, number>();
  for (const e of entries) {
    if (e.deletedAt || e.habitId !== habitId) continue;
    sums.set(e.logicalDate, (sums.get(e.logicalDate) ?? 0) + e.value);
  }
  // FR-LOG-02: el total diario nunca es negativo.
  for (const [k, v] of sums) sums.set(k, Math.max(0, round3(v)));
  return sums;
}

export function overrideIndex(overrides: OverrideLike[], habitId: string): Map<string, OverrideLike> {
  const idx = new Map<string, OverrideLike>();
  for (const o of overrides) if (!o.deletedAt && o.habitId === habitId) idx.set(o.ueKey, o);
  return idx;
}

function dailyGoal(v: HabitVersion): number {
  return v.goalType === 'binary' ? 1 : v.target;
}

/**
 * Evalúa todas las UE desde el inicio del hábito hasta `to` (por defecto, hoy).
 * Las UE futuras no se devuelven salvo que `to` sea posterior a hoy.
 */
export function evaluateHabit(input: EvaluationInput, range?: { from?: LocalDate; to?: LocalDate }): UEResult[] {
  const { habit, entries, overrides, vacations, moves, settings, today } = input;
  // Con media móvil cada día depende de los anteriores: se evalúa desde el inicio y se recorta.
  const averaged = habit.kind === 'build' && habit.versions.some((v) => v.average);
  const from = averaged ? habit.startDate : (range?.from ?? habit.startDate);
  const to = range?.to ?? today;
  const raw = generateUEs(habit, settings, from, to, moves);
  const totals = dailyTotals(entries, habit.id);
  const ovr = overrideIndex(overrides, habit.id);
  const out = raw.map((ue) => resolveUE(habit, ue, totals, ovr, vacations, today));
  if (!averaged) return out;
  const cut = range?.from;
  return applyAverage(out, today).filter((r) => !cut || r.end >= cut);
}

/**
 * Media móvil (FR-HAB-16). Recalcula en orden las UE diarias de las versiones que la
 * usan: la meta del día pasa a ser lo que falta para que la media de la ventana llegue
 * al objetivo. Decisiones propias: un día sin ningún registro nunca se cumple (el
 * excedente no rellena días vacíos), y mientras no haya N periodos se divide entre los
 * que haya. Omitidos, fallados a mano y vacaciones no entran en la ventana.
 */
function applyAverage(results: UEResult[], today: LocalDate): UEResult[] {
  const window: number[] = [];
  return results.map((r) => {
    const n = r.version.average;
    if (!n || r.kind !== 'day' || r.version.goalType !== 'numeric') return r;
    if (r.status === 'skipped' || r.status === 'failed' || r.status === 'vacation') return r;
    const prev = window.slice(-(n - 1));
    const sumPrev = prev.reduce((a, b) => a + b, 0);
    const k = prev.length + 1;
    const need = Math.max(0, r.version.target * k - sumPrev);
    window.push(r.value);
    const closed = r.end < today;
    const completed = r.value > 0 && r.value + EPS >= need;
    return {
      ...r,
      status: completed ? 'completed' : closed ? 'missed' : 'pending',
      final: completed || closed,
      required: round3(need),
      progress: round3(need > 0 ? Math.min(1, r.value / need) : r.value > 0 ? 1 : 0),
      average: { value: round3((sumPrev + r.value) / k), window: k },
    };
  });
}

function resolveUE(
  habit: HabitDef,
  ue: RawUE,
  totals: Map<LocalDate, number>,
  ovr: Map<string, OverrideLike>,
  vacations: VacationLike[],
  today: LocalDate,
): UEResult {
  const v = ue.version;
  const quit = habit.kind === 'quit';
  let value = 0;
  let required: number;
  let vacationAll: boolean;

  if (ue.kind === 'day') {
    value = totals.get(ue.start) ?? 0;
    required = quit ? v.target : dailyGoal(v);
    vacationAll = inVacation(vacations, habit.id, ue.start);
  } else {
    const schedule = v.schedule as Extract<Schedule, { type: 'weekly_quota' | 'monthly_quota' }>;
    let effDays = 0;
    let activeDays = 0;
    for (const d of eachDay(ue.activeStart, ue.activeEnd)) {
      activeDays++;
      if (!inVacation(vacations, habit.id, d)) effDays++;
      const t = totals.get(d) ?? 0;
      if (schedule.mode === 'times') value += t + EPS >= dailyGoal(v) ? 1 : 0;
      else value += t;
    }
    value = round3(value);
    vacationAll = activeDays > 0 && effDays === 0;
    const base = schedule.mode === 'times' ? (schedule.times ?? 1) : v.target;
    const factor = effDays / ue.nominal;
    if (schedule.mode === 'times') required = Math.ceil(base * factor - EPS);
    else required = factor >= 1 ? base : Math.ceil(base * factor * 1000 - EPS) / 1000;
    if (!vacationAll && !quit) required = Math.max(required, schedule.mode === 'times' ? 1 : 0.001);
  }

  const open = ue.start <= today && today <= ue.end;
  const closed = ue.end < today;
  const override = ovr.get(ue.key);
  let status: UEStatus;
  let final = closed;

  if (override?.kind === 'failed') {
    status = 'failed';
    final = true;
  } else if (override?.kind === 'skipped') {
    status = 'skipped';
    final = true;
  } else if (!quit && value + EPS >= required && required > 0) {
    status = 'completed';
    final = true;
  } else if (quit && value > required + EPS) {
    status = 'missed';
    final = true;
  } else if (vacationAll) {
    status = 'vacation';
  } else if (quit && closed) {
    status = 'completed';
  } else if (closed) {
    status = 'missed';
  } else {
    status = 'pending';
  }

  const progress = required > 0 ? Math.min(1, value / required) : quit ? (value > 0 ? 1 : 0) : 0;
  return {
    key: ue.key,
    kind: ue.kind,
    start: ue.start,
    end: ue.end,
    activeStart: ue.activeStart,
    activeEnd: ue.activeEnd,
    status,
    value,
    required: round3(required),
    progress: round3(progress),
    open,
    final,
    version: v,
  };
}

/** Estado de un día concreto para el calendario (incluye no programado y fuera de rango). */
export function statusOnDate(
  habit: HabitDef,
  results: UEResult[],
  d: LocalDate,
): { status: UEStatus; ue: UEResult | null } {
  if (d < habit.startDate || d > activeEnd(habit)) return { status: 'out_of_range', ue: null };
  const ue = results.find((r) => r.activeStart <= d && d <= r.activeEnd) ?? null;
  if (!ue) return { status: 'not_scheduled', ue: null };
  return { status: ue.status, ue };
}

/** Clave de la UE que contiene `d` (para anulaciones de omitir/fallar). */
export function ueKeyForDate(habit: HabitDef, settings: EngineSettings, d: LocalDate, moves?: MoveLike[]): string | null {
  const ues = generateUEs(habit, settings, d, d, moves);
  return ues[0]?.key ?? null;
}

export type TaskStatus = 'completed' | 'overdue' | 'pending';

export function taskStatus(habit: HabitDef, today: LocalDate): TaskStatus {
  if (habit.completedAt) return 'completed';
  if (habit.dueDate && habit.dueDate < today) return 'overdue';
  return 'pending';
}

export { isQuota };

/**
 * Motivos por los que un traslado no es válido (FR-SCH-12). Se comprueban en este
 * orden, y el primero que falla es el que se devuelve.
 */
export type MoveRejection =
  | 'same_day'
  | 'not_movable'
  | 'source_not_scheduled'
  | 'target_out_of_range'
  | 'target_scheduled';

/**
 * ¿Se puede trasladar la ocurrencia de `from` a `to`?
 *
 * Decisiones propias de HabitOS, deliberadamente más estrictas que mover una fecha sin más:
 *
 * - Solo se trasladan UE **diarias**. En una cuota semanal o mensual el día concreto no
 *   es una obligación, así que moverlo no significa nada, y moverlo a otro periodo
 *   falsearía la cuota.
 * - El origen tiene que estar programado ahora mismo y el destino no, para que un traslado
 *   nunca cree ni destruya obligaciones: siempre mueve exactamente una.
 * - El destino tiene que caer dentro del rango activo del hábito.
 * - No se encadenan traslados: si el destino ya es origen o destino de otro, se rechaza.
 */
export function checkMove(
  habit: HabitDef,
  settings: EngineSettings,
  moves: MoveLike[] | undefined,
  from: LocalDate,
  to: LocalDate,
): MoveRejection | null {
  if (from === to) return 'same_day';
  if (habit.kind === 'task') return 'not_movable';

  const source = generateUEs(habit, settings, from, from, moves);
  const ue = source[0];
  if (!ue || ue.start !== from) return 'source_not_scheduled';
  if (ue.kind !== 'day') return 'not_movable';

  if (to < habit.startDate || to > activeEnd(habit)) return 'target_out_of_range';
  const mv = indexMoves(moves, habit.id);
  if (mv.out.has(to) || mv.in.has(to)) return 'target_scheduled';
  if (generateUEs(habit, settings, to, to, moves).length > 0) return 'target_scheduled';
  return null;
}
